<script lang="ts">
  /**
   * Shown each time the app opens: choose the music and go. The first time (or when the terms
   * change) it also welcomes the visitor and asks them to agree to the terms; after that it's a
   * short "welcome back" that Enter, Esc or a click outside gets past. Its button is the click
   * browsers need before they play sound, so it starts the chosen music too.
   */
  import { onMount, untrack } from 'svelte';
  import type { AppController } from '../../app/controller.svelte';
  import type { SourceKind } from '../../engine/Engine';
  import type { IconName } from '../icons';
  import Icon from './Icon.svelte';
  import TermsText from './TermsText.svelte';
  import Toggle from './Toggle.svelte';

  let { ctl }: { ctl: AppController } = $props();

  type Choice = SourceKind | 'later';
  /** Agreed to the current terms before: the short version. */
  const returning = untrack(() => ctl.termsAccepted);
  let choice = $state<Choice>(untrack(() => ctl.welcomeSource));
  let files = $state<File[]>([]);
  /** Offer the tour to people who haven't seen it. */
  let tour = $state(untrack(() => !ctl.tourSeen));
  let showTerms = $state(false);
  let fileInput: HTMLInputElement;
  let startButton: HTMLButtonElement;

  const options = $derived(
    [
      { id: 'demo', icon: 'play', label: 'Demo groove', hint: 'A built-in track. No permissions.' },
      ctl.caps.microphone && {
        id: 'mic',
        icon: 'mic',
        label: 'Microphone',
        hint: 'Music playing in the room. Your browser will ask first.',
      },
      {
        id: 'file',
        icon: 'music',
        label: files.length
          ? `${files.length} file${files.length > 1 ? 's' : ''} chosen`
          : 'Files…',
        hint: 'Your own music, from this device.',
      },
      ctl.caps.tabAudio && {
        id: 'tab',
        icon: 'tab',
        label: 'Tab audio',
        hint: 'Spotify, YouTube… playing in another tab.',
      },
      { id: 'later', icon: 'clock', label: 'Decide later', hint: 'Look around first.' },
    ].filter((o): o is { id: Choice; icon: IconName; label: string; hint: string } => !!o),
  );

  // The last choice may not exist in this browser (tab audio in Firefox, say).
  $effect(() => {
    if (!options.some((o) => o.id === choice)) choice = 'demo';
  });

  onMount(() => startButton.focus());

  function pick(id: Choice) {
    if (id === 'file') fileInput.click();
    else choice = id;
  }

  const canStart = $derived(choice !== 'file' || files.length > 0);

  function start() {
    void ctl.acceptWelcome({ source: choice === 'later' ? null : choice, files, tour });
  }

  function onkey(e: KeyboardEvent) {
    if (e.key !== 'Escape' || !returning) return;
    e.preventDefault();
    e.stopPropagation();
    ctl.dismissWelcome();
  }
</script>

<svelte:window onkeydown={onkey} />

<div
  class="backdrop"
  role="presentation"
  onclick={(e) => {
    if (e.target === e.currentTarget) ctl.dismissWelcome();
  }}
>
  <div
    class="dialog"
    class:returning
    role="dialog"
    aria-modal="true"
    aria-labelledby="welcome-title"
    data-testid="welcome"
    data-returning={returning}
  >
    {#if returning}
      <div class="head">
        <h2 id="welcome-title">Welcome back</h2>
        <a
          class="docs-link"
          href="https://docs.glowtrope.com/"
          target="_blank"
          rel="noopener"
          data-testid="welcome-docs">Docs <Icon name="external" /></a
        >
      </div>
    {:else}
      <img
        class="hero"
        src={`${import.meta.env.BASE_URL}tour/welcome.jpg`}
        alt=""
        width="640"
        height="400"
      />
      <h2 id="welcome-title">Welcome to Glowtrope</h2>
      <p class="lede">
        Turn any music into visuals that move with the beat, right here in your browser. Every
        feature is free to use, with no limits, no watermarks and nothing to install, and your music
        never leaves your device.
      </p>
      <a
        class="docs"
        href="https://docs.glowtrope.com/"
        target="_blank"
        rel="noopener"
        data-testid="welcome-docs"
      >
        <span class="icon"><Icon name="book" /></span>
        <span>
          <strong>Learn more about Glowtrope on our docs page</strong>
          <small>Guides to every feature, with screenshots. Opens in a new tab.</small>
        </span>
        <span class="arrow"><Icon name="external" /></span>
      </a>
    {/if}

    <h3 id="music-title">Choose your music</h3>
    <div class="choices" role="radiogroup" aria-labelledby="music-title">
      {#each options as o (o.id)}
        <button
          role="radio"
          aria-checked={choice === o.id}
          class="choice"
          class:on={choice === o.id}
          data-testid={`welcome-${o.id}`}
          onclick={() => pick(o.id)}
        >
          <span class="icon"><Icon name={o.icon} /></span>
          <span><strong>{o.label}</strong><small>{o.hint}</small></span>
        </button>
      {/each}
    </div>
    <input
      bind:this={fileInput}
      class="visually-hidden"
      aria-label="Choose files"
      type="file"
      accept="audio/*"
      multiple
      tabindex="-1"
      data-testid="welcome-file-input"
      onchange={(e) => {
        const f = [...(e.currentTarget.files ?? [])];
        e.currentTarget.value = '';
        if (f.length) {
          files = f;
          choice = 'file';
        }
      }}
    />

    <div class="switches">
      <Toggle
        label="Limit flashing (photosensitivity-safe mode)"
        checked={ctl.settings.safeMode}
        testid="welcome-safe-mode"
        onchange={(v) => ctl.setSetting('safeMode', v)}
      />
      {#if !returning || !ctl.tourSeen}
        <Toggle
          label="Show me around (a short tour)"
          checked={tour}
          testid="welcome-tour"
          onchange={(v) => (tour = v)}
        />
      {/if}
      {#if returning}
        <Toggle
          label="Show this each time the app opens"
          checked={ctl.settings.showWelcome}
          testid="welcome-show-each-time"
          onchange={(v) => ctl.setSetting('showWelcome', v)}
        />
      {/if}
    </div>

    {#if !returning}
      <h3>Terms of use</h3>
      <p class="summary">
        Free for personal, noncommercial use. Commercial use, or reusing any of the code
        commercially, needs a paid license. It contains flashing images.
      </p>
    {/if}
    {#if !returning || showTerms}
      <!-- Focusable so the terms can be scrolled from the keyboard. -->
      <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
      <div class="terms" tabindex="0" role="region" aria-label="Terms of use">
        <TermsText />
      </div>
    {/if}

    <div class="actions">
      <span class="small muted">
        {#if returning}
          By continuing you agree to the
          <button
            class="link"
            aria-expanded={showTerms}
            data-testid="welcome-show-terms"
            onclick={() => (showTerms = !showTerms)}>terms of use</button
          > and privacy policy.
        {:else}
          By continuing you agree to the terms of use and privacy policy.
        {/if}
      </span>
      <button
        class="primary"
        bind:this={startButton}
        disabled={!canStart || ctl.busy}
        data-testid="welcome-agree"
        onclick={start}>{returning ? 'Start' : 'Agree and start'}</button
      >
    </div>
    <p class="credit small muted" data-testid="welcome-credit">
      © 2026 Mark Milhouse ·
      <a href="https://markmilhouse.com/" target="_blank" rel="noopener author">markmilhouse.com</a>
    </p>
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 64;
    display: grid;
    place-items: center;
    padding: 16px;
    background: rgba(5, 5, 8, 0.55);
  }
  .dialog {
    width: min(560px, 100%);
    max-height: calc(100vh - 32px);
    overflow: auto;
    padding: 0 20px 16px;
    border-radius: var(--radius);
    border: 1px solid var(--border);
    background: var(--panel-solid);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  }
  .dialog.returning {
    padding-top: 16px;
  }
  .hero {
    display: block;
    width: calc(100% + 40px);
    height: 140px;
    margin: 0 -20px 14px;
    object-fit: cover;
  }
  h2 {
    margin: 0 0 6px;
    font-size: 20px;
  }
  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
  }
  .head h2 {
    margin: 0;
  }
  .docs-link {
    color: var(--accent);
    font-size: 13px;
    text-decoration: none;
  }
  .docs-link:hover {
    text-decoration: underline;
  }
  .lede {
    margin: 0 0 16px;
    color: var(--muted);
    font-size: 13.5px;
    line-height: 1.5;
  }
  .docs {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0 0 18px;
    padding: 14px 16px;
    border-radius: var(--radius);
    border: 1px solid color-mix(in srgb, var(--accent) 55%, transparent);
    background: color-mix(in srgb, var(--accent) 12%, transparent);
    color: inherit;
    text-decoration: none;
    transition:
      background 0.15s,
      border-color 0.15s;
  }
  .docs:hover {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 22%, transparent);
  }
  .docs:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  .docs strong {
    display: block;
    font-size: 15px;
  }
  .docs small {
    display: block;
    margin-top: 2px;
    color: var(--muted);
    font-size: 12px;
  }
  .docs .icon {
    font-size: 22px;
    width: 28px;
  }
  .docs .arrow {
    margin-left: auto;
    font-size: 18px;
    color: var(--accent);
  }
  h3 {
    margin: 0 0 8px;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--muted);
  }
  .choices {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 8px;
    margin-bottom: 14px;
  }
  .choice {
    display: flex;
    gap: 10px;
    align-items: center;
    text-align: left;
    padding: 9px 12px;
  }
  .choice.on {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 16%, transparent);
  }
  .choice strong {
    display: block;
    font-size: 13.5px;
  }
  .choice small {
    display: block;
    color: var(--muted);
    font-size: 11.5px;
    margin-top: 1px;
  }
  .icon {
    width: 22px;
    font-size: 16px;
    text-align: center;
    flex: none;
  }
  .switches {
    display: grid;
    gap: 8px;
    margin-bottom: 16px;
  }
  .summary {
    margin: 0 0 8px;
    font-size: 13px;
    line-height: 1.5;
  }
  .terms {
    max-height: 150px;
    overflow: auto;
    padding: 10px 12px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    background: rgba(255, 255, 255, 0.03);
  }
  .actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 14px;
  }
  .credit {
    margin: 14px 0 0;
    text-align: center;
  }
  .credit a {
    color: inherit;
  }
  .link {
    padding: 0;
    border: 0;
    background: none;
    color: var(--accent);
    font: inherit;
    text-decoration: underline;
    cursor: pointer;
  }
  @media (max-height: 640px) {
    .hero {
      display: none;
    }
    .dialog {
      padding-top: 16px;
    }
  }
</style>
